import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { onNextFrame } from "../../utils/_private/animation";
import { classNames } from "../../utils/helpers";
import { trapFocus } from "../../utils/_private/a11y";
import { useToggle } from "../../hooks/_private/useToggle";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { useKeyboardCallback } from "../../hooks/_private/useKeyboardCallback";
import { useScrollLock } from "../../hooks/_private/useScrollLock";
import { useIsDismissible } from "../../hooks/_private/useIsDismissible";
import { ESC } from "../../constants/keys";
import { PortalProvider as Portal } from "../_private/portal";
import s from './Backdrop.module.css';
export const Backdrop = (props) => {
    const { open, children, transparent, onClose, className, attributes, opacity } = props;
    const [mounted, setMounted] = React.useState(false);
    const [mouseDownY, setMouseDownY] = React.useState(0);
    const contentRef = React.useRef(null);
    const rootRef = React.useRef(null);
    const isMouseDownValidRef = React.useRef(false);
    const releaseFocusRef = React.useRef(null);
    const { lockScroll, unlockScroll } = useScrollLock();
    const { active: rendered, activate: render, deactivate: remove } = useToggle(open || false);
    const { active: visible, activate: show, deactivate: hide } = useToggle(open || false);
    const isDismissible = useIsDismissible(open, contentRef);
    const rootClassNames = classNames(s.root, visible && s['--visible'], transparent && s['--transparent'], className);
    const isInsideChild = (el) => {
        if (!contentRef.current)
            return;
        const firstChild = contentRef.current.firstChild;
        if (!firstChild)
            return;
        return firstChild.contains(el);
    };
    const trapFocusInside = React.useCallback(() => {
        if (!contentRef.current)
            return;
        releaseFocusRef.current = trapFocus(contentRef.current, {
            focusFirstTrapElement: props.focusFirstTrapElement,
        });
    }, [props.focusFirstTrapElement]);
    const cancelTrapFocus = () => {
        if (!releaseFocusRef.current)
            return;
        releaseFocusRef.current();
        releaseFocusRef.current = null;
    };
    const close = React.useCallback(() => {
        if (!visible || !isDismissible())
            return;
        if (onClose)
            onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isDismissible]);
    const handleMouseDown = (event) => {
        setMouseDownY(event.clientY);
        isMouseDownValidRef.current = !isInsideChild(event.target);
    };
    const handleMouseUp = (event) => {
        const mouseUpY = event.clientY;
        const scrollClick = Math.abs(mouseDownY - mouseUpY) > 10 && event.clientX > window.outerWidth - 20;
        const isMouseUpValid = !isInsideChild(event.target) && !scrollClick;
        const shouldClose = isMouseDownValidRef.current && isMouseUpValid && !transparent;
        if (!shouldClose)
            return;
        close();
    };
    const handleTransitionEnd = () => {
        if (visible)
            return;
        if (!transparent)
            unlockScroll();
        remove();
    };
    useKeyboardCallback(ESC, close, [close]);
    React.useEffect(() => {
        if (open && !rendered)
            render();
        if (!open && rendered)
            hide();
    }, [open, render, hide, rendered]);
    // Show backdrop after it was rendered
    React.useEffect(() => {
        if (!rendered)
            return;
        if (!transparent)
            lockScroll();
        onNextFrame(() => show());
    }, [rendered, show, lockScroll, transparent]);
    React.useEffect(() => {
        if (!rendered || !mounted)
            return;
        trapFocusInside();
        return () => cancelTrapFocus();
    }, [rendered, mounted, trapFocusInside]);
    // Unlock scroll on unmount
    React.useEffect(() => {
        return () => {
            if (!transparent)
                unlockScroll();
        };
    }, [unlockScroll, transparent]);
    useIsomorphicLayoutEffect(() => {
        setMounted(true);
    }, []);
    if (!rendered || !mounted)
        return null;
    return (_jsx(Portal, { scopeRef: rootRef, children: _jsx("div", Object.assign({ "data-testid": "st-backdrop" }, attributes, { ref: rootRef, role: (attributes === null || attributes === void 0 ? void 0 : attributes.role) || 'button', tabIndex: -1, className: rootClassNames, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, onTransitionEnd: handleTransitionEnd, children: _jsx("div", { className: s.wrapper, style: { '--st-backdrop-opacity': opacity }, children: _jsx("div", { className: s.inner, children: _jsx("div", { className: s.content, ref: contentRef, children: typeof children === 'function' ? children({ open: visible }) : children }) }) }) })) }));
};
